export const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

const hasWildcard = (pattern: string) => pattern.includes("*") || pattern.includes("?");

export const normalizePattern = (pattern: string | null | undefined) => {
  const trimmed = (pattern ?? "").trim();
  if (!trimmed) {
    return "";
  }
  return hasWildcard(trimmed) ? trimmed : `*${trimmed}*`;
};

// Escape regex special characters including glob wildcards (* and ?)
const escapeRegex = (value: string) => value.replace(/[.+^${}()|[\]\\*?]/g, "\\$&");

const patternToRegex = (pattern: string) =>
  new RegExp(
    // First escape all special chars (including * and ?), then convert escaped globs to regex equivalents
    `^${escapeRegex(pattern).replace(/\\\*/g, ".*").replace(/\\\?/g, ".")}$`,
    "i",
  );

export const matchesPattern = (fileName: string, pattern: string | null | undefined) => {
  const normalized = normalizePattern(pattern);
  if (!normalized) {
    return false;
  }
  return patternToRegex(normalized).test(fileName);
};

export const isPdfFile = (fileName: string) => fileName.toLowerCase().endsWith(".pdf");

export const isImageFile = (fileName: string) =>
  IMAGE_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));

export const matchesType = (fileName: string, type: string | null | undefined) => {
  if (!type) {
    return isPdfFile(fileName) || isImageFile(fileName);
  }
  if (type === "pdf") {
    return isPdfFile(fileName);
  }
  if (type === "image") {
    return isImageFile(fileName);
  }
  return true;
};

export const stripExtension = (fileName: string) => fileName.replace(/\.[^/.]+$/, "");

export const guessTypeFromFileName = (fileName: string) => {
  if (isPdfFile(fileName)) {
    return "pdf";
  }
  if (isImageFile(fileName)) {
    return "image";
  }
  return null;
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const createCustomId = (seed: string) => {
  const slug = toSlug(seed) || "custom";
  return `custom-${slug}-${Date.now().toString(36)}`;
};

// Extract suffix pattern from filename by removing common prefixes (timestamps, Notes_note_ID_)
export const extractFileSuffix = (fileName: string): string => {
  // Remove timestamp prefix like "260115155408_" (12 digits + underscore)
  let suffix = fileName.replace(/^\d{12}_/, "");
  // Remove Notes_note_ID_ prefix like "Notes_note_325210_"
  suffix = suffix.replace(/^Notes_note_\d+_/, "");
  return suffix;
};

export type DetectedFileGroup = {
  suffix: string;
  files: string[];
  isPps: boolean;
  suggestedPattern: string;
};

export const groupDetectedFiles = (
  files: string[],
): { ppsFiles: DetectedFileGroup[]; otherFiles: DetectedFileGroup[] } => {
  const ppsMap = new Map<string, string[]>();
  const otherMap = new Map<string, string[]>();

  files.forEach((fileName) => {
    const isPps =
      fileName.startsWith("PPS") || fileName.startsWith("pps") || fileName.startsWith("SKY_");
    const suffix = isPps ? fileName : extractFileSuffix(fileName);
    const map = isPps ? ppsMap : otherMap;

    const existing = map.get(suffix) || [];
    existing.push(fileName);
    map.set(suffix, existing);
  });

  const createGroup = (suffix: string, fileList: string[], isPps: boolean): DetectedFileGroup => {
    const baseName = stripExtension(suffix) || suffix;
    const suggestedPattern = isPps ? suffix : `*${baseName}*`;
    return { suffix, files: fileList, isPps, suggestedPattern };
  };

  const ppsFiles = Array.from(ppsMap.entries())
    .map(([suffix, fileList]) => createGroup(suffix, fileList, true))
    .sort((a, b) => b.files.length - a.files.length);

  const otherFiles = Array.from(otherMap.entries())
    .map(([suffix, fileList]) => createGroup(suffix, fileList, false))
    .sort((a, b) => b.files.length - a.files.length);

  return { ppsFiles, otherFiles };
};

export const extractPatternCore = (pattern: string | null | undefined) => {
  const normalized = normalizePattern(pattern).toLowerCase();
  if (!normalized) {
    return "";
  }
  if (normalized.startsWith("*") && normalized.endsWith("*") && normalized.length > 2) {
    return normalized.slice(1, -1).trim();
  }
  return normalized.replace(/\*/g, "").replace(/\?/g, "").trim();
};

export const coversPattern = (excludePattern: string, smartPattern: string) => {
  const excludeCore = extractPatternCore(excludePattern);
  const smartCore = extractPatternCore(smartPattern);
  if (!excludeCore || !smartCore) {
    return normalizePattern(excludePattern).toLowerCase() === normalizePattern(smartPattern).toLowerCase();
  }
  // Directional: excludePattern covers smartPattern when every file matching smartPattern
  // also matches excludePattern. For *X* globs this requires excludeCore to be a substring
  // of smartCore (broader exclude catches everything the narrower smart pattern would).
  return smartCore.includes(excludeCore);
};

export const buildLocalWarnings = (documents: PdfDocumentOption[]) => {
  const warnings: string[] = [];
  const smartDocs = documents
    .filter((doc) => doc.enabled !== false && doc.id.startsWith("smartofp-") && doc.pattern)
    .map((doc) => ({
      id: doc.id,
      name: doc.name,
      patternRaw: doc.pattern ?? "",
      pattern: normalizePattern(doc.pattern).toLowerCase(),
    }));

  documents.forEach((doc) => {
    if (doc.enabled === false) {
      return;
    }
    const patternRaw = doc.pattern ?? "";
    const normalizedPattern = normalizePattern(patternRaw).toLowerCase();
    if (!normalizedPattern.startsWith("*") || !normalizedPattern.endsWith("*") || normalizedPattern.length <= 2) {
      return;
    }
    const core = normalizedPattern.slice(1, -1).trim();
    if (!core) {
      return;
    }

    const overlaps = smartDocs.filter((smart) => smart.id !== doc.id && smart.pattern.includes(core));
    if (!overlaps.length) {
      return;
    }

    const excludePatterns = Array.isArray(doc.excludePatterns) ? doc.excludePatterns : [];
    const uncovered = overlaps.filter(
      (smart) => !excludePatterns.some((exclude) => coversPattern(exclude, smart.patternRaw)),
    );

    const docNames = overlaps
      .map((smart) => `\`${smart.name}\`${smart.patternRaw ? ` (${smart.patternRaw})` : ""}`)
      .join(" and ");
    const genericLabel = doc.name || doc.id || "generic entry";
    const genericPattern = patternRaw || "no pattern";

    if (uncovered.length > 0) {
      const uncoveredPatterns = uncovered
        .map((smart) => smart.patternRaw)
        .filter((value) => value)
        .map((value) => `\`${value}\``)
        .join(", ");

      warnings.push(
        `Potential duplicate match: \`${genericLabel}\` (${genericPattern}) overlaps with smartOFP documents ${docNames}. ` +
          (uncoveredPatterns ? `The following smartOFP patterns are not excluded: ${uncoveredPatterns}. ` : "") +
          "This can cause the same file to be included twice. " +
          "Fix by adding these patterns to 'Exclude patterns', disabling the generic entry, or narrowing its pattern.",
      );
    }
  });

  return warnings;
};

export const reorderDocuments = <T extends { order: number }>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number,
) => {
  const updated = [...items];
  const [moved] = updated.splice(sourceIndex, 1);
  updated.splice(destinationIndex, 0, moved);

  return updated.map((item, index) => ({ ...item, order: index + 1 }));
};

export const resequenceDocuments = <T extends { order: number }>(items: T[]) =>
  items.map((item, index) => ({ ...item, order: index + 1 }));

export const buildAvailableSystemDocuments = (
  activeDocuments: PdfDocumentOption[],
  systemCatalogDocuments: PdfDocumentOption[],
) => {
  const activeIds = new Set(activeDocuments.map((doc) => doc.id));

  return systemCatalogDocuments
    .filter((doc) => !activeIds.has(doc.id))
    .map((doc, index) => ({
      ...doc,
      enabled: false,
      order: index + 1,
    }));
};

export const getUnmatchedDetectedFiles = (files: string[], documents: PdfDocumentOption[]) => {
  if (!files.length) {
    return [];
  }

  return files.filter((fileName) =>
    !documents.some((doc) => {
      if (doc.enabled === false) {
        return false;
      }
      if (!matchesPattern(fileName, doc.pattern)) {
        return false;
      }
      if (Array.isArray(doc.excludePatterns)) {
        const excluded = doc.excludePatterns.some((pattern) => matchesPattern(fileName, pattern));
        if (excluded) {
          return false;
        }
      }
      return matchesType(fileName, doc.type);
    }),
  );
};

export type MatchStats = {
  totalMatches: number;
  pdfMatches: number;
  imageMatches: number;
  examples: string[];
};

export const computeMatchStats = (
  files: string[],
  doc: {
    pattern?: string | null;
    type?: string | null;
    excludePatterns?: string[];
  },
): MatchStats => {
  if (!files.length) {
    return { totalMatches: 0, pdfMatches: 0, imageMatches: 0, examples: [] };
  }

  const matched = files.filter((fileName) => {
    if (!matchesPattern(fileName, doc.pattern)) {
      return false;
    }
    if (Array.isArray(doc.excludePatterns)) {
      const excluded = doc.excludePatterns.some((pattern) => matchesPattern(fileName, pattern));
      if (excluded) {
        return false;
      }
    }
    return matchesType(fileName, doc.type);
  });

  const pdfMatches = matched.filter((fileName) => isPdfFile(fileName)).length;
  const imageMatches = matched.filter((fileName) => isImageFile(fileName)).length;

  return {
    totalMatches: matched.length,
    pdfMatches,
    imageMatches,
    examples: matched.slice(0, 3),
  };
};

const uniq = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

const isSmartOfpDoc = (doc: PdfDocumentOption) => doc.id.startsWith("smartofp-");
const isCustomDoc = (doc: PdfDocumentOption) => doc.id.startsWith("custom-");

const isBroadPattern = (pattern: string) => {
  const normalized = normalizePattern(pattern).toLowerCase();
  return normalized.startsWith("*") && normalized.endsWith("*") && normalized.length > 2;
};

type AutoExcludeOptions = {
  processDisabledDocs?: boolean;
  includeDisabledSmartDocs?: boolean;
};

export const applySmartOfpAutoExcludes = (
  documents: PdfDocumentOption[],
  { processDisabledDocs = false, includeDisabledSmartDocs = false }: AutoExcludeOptions = {},
) => {
  const smartDocsAll = documents
    .filter((doc) => isSmartOfpDoc(doc) && doc.pattern)
    .map((doc) => ({ patternRaw: doc.pattern ?? "", pattern: normalizePattern(doc.pattern).toLowerCase(), enabled: doc.enabled !== false }));

  const smartPatternsAll = smartDocsAll.map((doc) => doc.patternRaw).filter(Boolean);
  const smartDocsForOverlap = includeDisabledSmartDocs
    ? smartDocsAll
    : smartDocsAll.filter((doc) => doc.enabled);

  return documents.map((doc) => {
    if (!processDisabledDocs && doc.enabled === false) {
      return doc;
    }
    if (isSmartOfpDoc(doc) || isCustomDoc(doc)) {
      return doc;
    }
    const patternRaw = doc.pattern ?? "";
    if (!patternRaw || !isBroadPattern(patternRaw)) {
      return doc;
    }

    const core = extractPatternCore(patternRaw);
    if (!core) {
      return doc;
    }

    const overlaps = smartDocsForOverlap
      .filter((smart) => smart.pattern.includes(core.toLowerCase()))
      .map((smart) => smart.patternRaw)
      .filter(Boolean);

    const hasExcludeOverrides = Object.prototype.hasOwnProperty.call(doc, "excludePatterns");
    const existing = Array.isArray(doc.excludePatterns) ? doc.excludePatterns : [];
    // Identify user-defined excludes by removing only those that exactly match a known
    // smart pattern (auto-generated excludes are exact copies of smart patterns).
    const nonSmart = existing.filter(
      (exclude) => !smartPatternsAll.some(
        (smartPattern) => normalizePattern(exclude).toLowerCase() === normalizePattern(smartPattern).toLowerCase(),
      ),
    );
    const nextExcludePatterns = uniq([...nonSmart, ...overlaps]);

    if (nextExcludePatterns.length === 0 && !hasExcludeOverrides) {
      return doc;
    }

    return {
      ...doc,
      excludePatterns: nextExcludePatterns,
    };
  });
};

export const applyTypeGuess = (documents: PdfDocumentOption[], files: string[]) => {
  if (!files.length) {
    return documents;
  }

  return documents.map((doc) => {
    if (doc.enabled === false) {
      return doc;
    }

    // Skip documents with an explicitly configured type (including null = "Any").
    // Only guess when type is undefined (never set).
    if (doc.type !== undefined) {
      return doc;
    }

    const stats = computeMatchStats(files, {
      pattern: doc.pattern,
      type: null,
      excludePatterns: doc.excludePatterns,
    });

    if (stats.pdfMatches > 0 && stats.imageMatches === 0) {
      return { ...doc, type: "pdf" };
    }
    if (stats.imageMatches > 0 && stats.pdfMatches === 0) {
      return { ...doc, type: "image" };
    }

    return { ...doc, type: null };
  });
};
